import React, { useState } from 'react';
import { Phone, Menu, X, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isPartnersPage = location.pathname === '/partners';
  const isGetQuotePage = location.pathname === '/get-quote';

  const navLinks = [
    { name: 'Home', href: isHomePage ? '#home' : '/' },
    { name: 'Services', href: isHomePage ? '#services' : '/#services' },
    { name: 'Why Us', href: isHomePage ? '#why-us' : '/#why-us' },
    { name: 'Reviews', href: isHomePage ? '#reviews' : '/#reviews' },
    { name: 'Blog', to: '/blog' },
    { name: 'Quote', to: '/get-quote' },
    { name: 'Contact', href: isHomePage ? '#contact' : '/#contact' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      {/* Top bar */}
      <div className="bg-[#0f1729] text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-[#7C83E0] transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-medium">{companyInfo.phone}</span>
            </a>
            <a href={`mailto:${companyInfo.email}`} className="hidden sm:flex items-center gap-2 hover:text-[#7C83E0] transition-colors">
              <Mail className="w-4 h-4" />
              <span>{companyInfo.email}</span>
            </a>
          </div>
          <span className="hidden md:block text-gray-400">Servicing Sussex and the South Coast</span>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={companyInfo.logo} 
              alt="HappyVan Logo" 
              className="w-36 md:w-44 h-auto object-contain"
              width="176"
              height="60"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => {
              const LinkTag = link.to ? Link : 'a';
              const linkProps = link.to ? { to: link.to } : { href: link.href };
              return (
                <LinkTag
                  key={link.name}
                  {...linkProps}
                  className="text-stone-600 hover:text-[#3B43AF] font-medium transition-colors relative group"
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-500 transition-all duration-300 group-hover:w-full"></span>
                </LinkTag>
              );
            })}
            <Link
              to="/partners"
              className={`font-medium transition-colors relative group ${isPartnersPage ? 'text-[#3B43AF]' : 'text-stone-600 hover:text-[#3B43AF]'}`}
            >
              Partners
              <span className={`absolute -bottom-1 left-0 h-0.5 bg-amber-500 transition-all duration-300 ${isPartnersPage ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-stone-700 hover:text-[#3B43AF] transition-colors"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t pt-4 animate-slide-up">
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => {
                const LinkTag = link.to ? Link : 'a';
                const linkProps = link.to ? { to: link.to } : { href: link.href };
                return (
                  <LinkTag
                    key={link.name}
                    {...linkProps}
                    onClick={() => setIsMenuOpen(false)}
                    className="text-stone-700 hover:text-[#3B43AF] font-medium py-2 transition-colors mobile-touch-target"
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </LinkTag>
                );
              })}
              <Link
                to="/partners"
                onClick={() => setIsMenuOpen(false)}
                className={`font-medium py-2 transition-colors mobile-touch-target ${isPartnersPage ? 'text-[#3B43AF]' : 'text-stone-700 hover:text-[#3B43AF]'}`}
              >
                Partners
              </Link>
              {isGetQuotePage ? (
                <Button 
                  asChild
                  className="bg-[#3B43AF] hover:bg-[#2f3691] text-white mt-2 py-6"
                >
                  <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} onClick={() => setIsMenuOpen(false)}>Call for a Quote</a>
                </Button>
              ) : (
                <Button 
                  asChild
                  className="bg-[#3B43AF] hover:bg-[#2f3691] text-white mt-2 py-6"
                >
                  <Link to="/get-quote" onClick={() => setIsMenuOpen(false)}>Get Free Quote</Link>
                </Button>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
